import React, { useState } from "react";
import { formatDayTime } from "../../api/shape.js";

/**
 * One version link: a real href opened in a new tab (a SharePoint
 * `_vti_history` URL the signed-in user's own session can open). A version
 * with no openable URL keeps its label (numbering stays true) but is not a link.
 */
export function VersionLink({ link }) {
  if (!link.url) return <span className="mono" style={{ fontSize: 10, color: "var(--t3)" }}>{link.label}</span>;
  return (
    <a href={link.url} target="_blank" rel="noopener noreferrer" className="mono" style={{ fontSize: 10, color: "var(--t3)", textDecoration: "underline" }}>
      {link.label}
    </a>
  );
}

/** The seven fixed column widths of the design (the Actions column takes the rest). */
export const COLUMN_WIDTHS = ["180px", "100px", "64px", "116px", "160px", "", "112px"];
export const TABLE_COLUMNS = ["Period", "Report", "Version", "Status", "Generated", "Actions", "History"];

/** Earlier versions of a row, from the loaded list or (on first expand) the lazy read. */
export function useEarlierVersions(row, onLoadVersions) {
  const [lazy, setLazy] = useState(null); // { loading, links, failed }
  const needsLoad = !row.historyLinks.length && row.versionsUrl && onLoadVersions;
  async function load() {
    if (!needsLoad || lazy) return;
    setLazy({ loading: true, links: [] });
    const res = await onLoadVersions(row.versionsUrl);
    const versions = (res && res.ok && res.body && res.body.versions) || [];
    const links = versions.filter(Boolean).map((v, i, all) => ({
      key: v.versionId || `v${i}`,
      label: `v${all.length - i}`,
      url: v.url || "",
      when: v.createdAtUtc ? formatDayTime(v.createdAtUtc) : "",
    }));
    setLazy({ loading: false, links: links.slice(1), failed: !(res && res.ok) });
  }
  return { links: row.historyLinks.length ? row.historyLinks : lazy ? lazy.links : [], loading: Boolean(lazy && lazy.loading), failed: Boolean(lazy && lazy.failed), load, canLoad: Boolean(needsLoad) };
}

function HistoryRows({ row, history }) {
  if (history.loading) {
    return (
      <tr className="wr-sub" data-testid={`report-history-${row.reportNumber}`}>
        <td>└ earlier versions</td>
        <td colSpan={6} className="wr-supers" role="status">Loading…</td>
      </tr>
    );
  }
  if (!history.links.length) {
    return (
      <tr className="wr-sub" data-testid={`report-history-${row.reportNumber}`}>
        <td>└ earlier versions</td>
        <td colSpan={6} className="wr-supers">{history.failed || !history.canLoad ? "Earlier versions could not be listed." : "No earlier versions."}</td>
      </tr>
    );
  }
  return history.links.map((link) => (
    <tr className="wr-sub" key={link.key} data-testid={`report-history-${row.reportNumber}-${link.label}`}>
      <td>└ earlier version</td>
      <td />
      <td className="mono">{link.label}</td>
      <td><span className="wr-supers">superseded</span></td>
      <td className="gen">{link.when}</td>
      <td>
        <div className="wr-act">
          {link.url ? (
            <a href={link.url} target="_blank" rel="noopener noreferrer">Open {link.label} in Word (read only)</a>
          ) : (
            <span className="wr-note">{link.label} has no link</span>
          )}
        </div>
      </td>
      <td />
    </tr>
  ));
}

/**
 * SPEC-CIS-REPORT-SCHEDULED-PUBLISH-001 section 6: the schedule line under an
 * Approved row (badge, stop reason, Schedule / Hold / Publish now) and the
 * "Published automatically" note under an Issued one. Not drawn when the
 * server offers no schedule and the row is not scheduled.
 */
export function ScheduleRow({ row, busy, onSchedule, onHold, onPublish }) {
  const sc = row.schedule;
  if (!sc) return null;
  if (sc.state === "issued") {
    return (
      <tr className="wr-sub" data-testid={`report-schedule-${row.reportNumber}`}>
        <td>└ publication</td>
        <td colSpan={6}><span className="wr-note" data-testid={`report-published-automatically-${row.reportNumber}`}>{sc.note}</span></td>
      </tr>
    );
  }
  return (
    <tr className="wr-sub" data-testid={`report-schedule-${row.reportNumber}`} data-schedule-state={sc.state}>
      <td>└ publication</td>
      <td colSpan={6}>
        <div className="wr-act" style={{ gridTemplateColumns: "auto auto auto 1fr", alignItems: "center", gap: 8 }}>
          {sc.badge ? (
            <span className={`wr-chip ${sc.state === "stopped" ? "draft" : "approved"}`} data-testid={`report-schedule-badge-${row.reportNumber}`}>{sc.badge}</span>
          ) : null}
          {sc.reason ? <span className="wr-note" data-testid={`report-schedule-reason-${row.reportNumber}`}>{sc.reason}</span> : null}
          {sc.canSchedule && onSchedule ? (
            <button type="button" className="publish" data-testid={`report-schedule-${row.reportNumber}-schedule`} disabled={busy} onClick={() => onSchedule(row)}>
              {sc.scheduleLabel || "Schedule"}
            </button>
          ) : null}
          {sc.canHold && onHold ? (
            <button type="button" className="retry" data-testid={`report-schedule-${row.reportNumber}-hold`} disabled={busy} onClick={() => onHold(row)}>
              Hold
            </button>
          ) : null}
          {sc.canPublishNow && onPublish && !row.pdfFailed ? (
            <button type="button" className="publish" data-testid={`report-schedule-${row.reportNumber}-publish-now`} disabled={busy} onClick={() => onPublish(row)}>
              Publish now
            </button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

function ReportRow({ row, current, busy, onApprove, onPublish, onRetryPdf, onSchedule, onHold, onLoadVersions, open, onToggle }) {
  const history = useEarlierVersions(row, onLoadVersions);
  const historyId = `wr-history-${row.reportNumber}`;
  const wordLabel = row.isDraft ? "Edit in Word" : "Open in Word";
  return (
    <>
      <tr className="wr-row" data-testid={`report-row-${row.reportNumber}`} data-current={current ? "true" : undefined} data-highlight={undefined}>
        <td title={row.periodText}>{row.periodText}</td>
        <td className="no">{row.reportText}</td>
        <td className="mono" data-testid="report-row-version">v{row.displayVersion}</td>
        <td><span className={`wr-chip ${row.statusTone}`}>{row.statusText}</span></td>
        <td className="gen">{row.generatedText}</td>
        <td>
          <div className="wr-act">
            {row.wordUrl ? (
              <a href={row.wordUrl} target="_blank" rel="noopener noreferrer" data-testid={`report-word-${row.reportNumber}`}>{wordLabel}</a>
            ) : (
              <span className="off" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", height: 34, border: "1px solid var(--wr-line)", borderRadius: 4 }} aria-disabled="true">{wordLabel}</span>
            )}
            {row.isDraft ? (
              <button type="button" className="approve" data-testid={`report-approve-${row.reportNumber}`} disabled={busy} onClick={() => onApprove(row)}>
                Approve
              </button>
            ) : row.pdfFailed ? (
              <button type="button" className="retry" data-testid="report-pdf-retry" title="PDF not created" disabled={busy || !onRetryPdf} onClick={() => onRetryPdf(row)}>
                Retry PDF
              </button>
            ) : row.isPublished && row.publishedUrl ? (
              <a href={row.publishedUrl} target="_blank" rel="noopener noreferrer" className="site" data-testid={`report-open-site-${row.reportNumber}`}>Open on project site</a>
            ) : row.pdfOpenUrl ? (
              <a href={row.pdfOpenUrl} target="_blank" rel="noopener noreferrer" data-testid="report-open-pdf">Open PDF</a>
            ) : (
              <span />
            )}
            {/* The report the steps are about publishes from its step 6; any OTHER approved report
                (an earlier week still unpublished) publishes from here, so none is stranded. */}
            {row.isApproved && !row.pdfFailed && onPublish && !current && !row.schedule ? (
              <button type="button" className="publish" data-testid={`report-publish-${row.reportNumber}`} disabled={busy} onClick={() => onPublish(row)}>
                Publish PDF
              </button>
            ) : row.pdfFailed && !current ? (
              <span className="wr-note" data-testid="report-pdf-missing">PDF not created</span>
            ) : (
              <span className="wr-note" title={row.note}>{row.note}</span>
            )}
          </div>
        </td>
        <td>
          {row.hasHistory ? (
            <button
              type="button"
              className="wr-hist"
              aria-expanded={open}
              aria-controls={historyId}
              data-testid={`report-history-toggle-${row.reportNumber}`}
              onClick={() => {
                if (!open) history.load();
                onToggle();
              }}
            >
              {open ? "Hide ▴" : `${row.historyCount} earlier ▾`}
            </button>
          ) : null}
        </td>
      </tr>
      <ScheduleRow row={row} busy={busy} onSchedule={onSchedule} onHold={onHold} onPublish={onPublish} />
      {open ? <HistoryRows row={row} history={history} /> : null}
    </>
  );
}

/**
 * The project's reports (design Main.dc.html "Reports for <code>"): a real
 * table with fixed column widths, scrolling sideways inside its own box below
 * its minimum width. The Actions cell is a fixed three-column grid
 * (Word | Approve / Open PDF / Retry PDF | note or Publish), so nothing can
 * overlap whatever the row says. Every Draft row carries Edit in Word and
 * Approve -- any draft can be edited and approved, not only the newest.
 */
export default function ReportsTable({ project, busyNumber, openHistory, onToggleHistory, onApprove, onPublish, onRetryPdf, onSchedule, onHold, onLoadVersions }) {
  const currentNumber = project.current ? project.current.reportNumber : null;
  return (
    <div className="wr-reports">
      <span className="wr-tablelabel" id={`wr-reports-label-${project.code}`}>Reports for {project.code}</span>
      <div className="wr-tablebox" role="region" aria-labelledby={`wr-reports-label-${project.code}`} tabIndex={0}>
        <table className="wr-table" data-testid={`report-table-${project.code}`}>
          <colgroup>
            {COLUMN_WIDTHS.map((w, i) => (w ? <col key={i} style={{ width: w }} /> : <col key={i} />))}
          </colgroup>
          <thead>
            <tr>
              {TABLE_COLUMNS.map((c) => (
                <th scope="col" key={c}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {project.archive.length === 0 ? (
              <tr>
                <td colSpan={7} className="wr-empty" style={{ height: "auto", fontFamily: "inherit", fontSize: 13 }}>No reports yet</td>
              </tr>
            ) : (
              project.archive.map((row) => (
                <ReportRow
                  key={row.key}
                  row={row}
                  current={row.reportNumber === currentNumber}
                  busy={busyNumber !== null && busyNumber !== undefined}
                  open={Boolean(openHistory[`${project.code}-${row.reportNumber}`])}
                  onToggle={() => onToggleHistory(`${project.code}-${row.reportNumber}`)}
                  onApprove={onApprove}
                  onPublish={onPublish}
                  onRetryPdf={onRetryPdf}
                  onSchedule={onSchedule}
                  onHold={onHold}
                  onLoadVersions={onLoadVersions}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
